import React from 'react';
import type {List} from 'immutable';
import qs from 'qs';
import {Link} from 'react-router';

import sessionStore from 'client/Session/SessionStore';
import {showGlobalLoginModal} from 'client/LogIn/utils';
import {isSearchMode} from 'client/components/AdvancedSearch/AdvancedSearch.utils';
import type {GuideTree} from 'client/components/ContentDiscovery/ManageRestrictionsModal';
import {StudyGuideItem} from 'client/generic/StudyGuideItem/StudyGuideItem.react';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {pluralize} from 'lib/stringUtils';
import {isUnlicensedTeacherOrAdmin} from 'lib/UserAccessUtil';

import {GuideLevelActions} from './GuideLevelActions';
import {GuideLevelHeadingIndicators, GuideLevelRestrictionBadge} from './GuideLevelIndicators';
import type {ThemeRefs} from './ThemeMenu/ThemeMenu';

interface GuideLevelCardProps {
  guideLevel: any;
  /**
   * The row that follows this one in the *displayed* list. The row's separator class and its
   * public-view lock both depend on the next row's nlevel, so the parent resolves the lookahead
   * instead of handing down the list and an index.
   */
  nextGuideLevel: any;
  subject: any;
  licensedSubjects: List<any>;
  locationQuery: Record<string, unknown>;
  usePublicView: boolean;
  isAssessment: boolean;
  hasTeacherAccess: boolean;
  hasAccessToCreateTemplate: boolean;
  partialCreditEnabled: boolean;
  guideLevelTree: GuideTree;
  teacherRestrictedGuideLevelIds: Set<string>;
  guideLevelIdsWithLockedDescendant: Set<string>;
  themeRefs: ThemeRefs;
  getMatchingQuestionCount: (guideLevel: any) => number;
  getStudyGuideLink: (
    linkToQuestions: string,
    queryString: string
  ) => {pathname: string; search: string; state: {from: string}};
  openUpgradeSubjectModal: () => void;
  onKeyPress: (e: React.KeyboardEvent, onClick: (e: React.KeyboardEvent) => void) => void;
}

/**
 * One row of the study guide's card list.
 *
 * The row's element type is precedence-resolved, not disjoint: the `else if (isButton)` override
 * deliberately does not re-check `guideLevelDisabled`, while the lock icon keys off it
 * independently — so a disabled row in a button context renders as a `role='button'` list item
 * *with* the lock. Every `StudyGuideItem.*` static has to stay a direct child at its current
 * nesting: `StudyGuideItem` clones only its own statics to inject `itemType`, which is what
 * produces the `sgi--<itemType>` styling.
 */
export const GuideLevelCard = ({
  guideLevel,
  nextGuideLevel,
  subject,
  licensedSubjects,
  locationQuery,
  usePublicView,
  isAssessment,
  hasTeacherAccess,
  hasAccessToCreateTemplate,
  partialCreditEnabled,
  guideLevelTree,
  teacherRestrictedGuideLevelIds,
  guideLevelIdsWithLockedDescendant,
  themeRefs,
  getMatchingQuestionCount,
  getStudyGuideLink,
  openUpgradeSubjectModal,
  onKeyPress
}: GuideLevelCardProps) => {
  let metadata;
  if (isSearchMode()) {
    const matchingQuestionsCount = getMatchingQuestionCount(guideLevel);
    metadata = (
      <span className='study-guide__search-result'>
        {`${matchingQuestionsCount} ${pluralize('question', matchingQuestionsCount)}`}
      </span>
    );
  } else {
    metadata = guideLevel.getQuestionMetadataForSubjectView(hasTeacherAccess);
  }

  let queryString = '';
  if (isSearchMode()) {
    const searchQuery = {
      ...locationQuery,
      page: 1
    };
    queryString += qs.stringify(searchQuery, {addQueryPrefix: true});
  }
  const linkToQuestions = guideLevel.getLinkToQuestions();
  const hasSubtopics = nextGuideLevel && nextGuideLevel.getNlevel() === guideLevel.getNlevel() + 1;
  const isLastOfTheme = nextGuideLevel && nextGuideLevel.getNlevel() === 1;
  /**
   * We add **1** here to account for the `<HeroUnit>` containing a `h1`.
   */
  const headingLevel = guideLevel.getNlevel() + 1;
  const isStudent = sessionStore.isStudent();
  const retailStudentWithAccess =
    isStudent && licensedSubjects.isEmpty() && subject.doesUserHaveAccess();
  const isStudentWithoutAccess = isStudent && !retailStudentWithAccess;
  const {isReady: isAccessCheckReady, value: isUnlicensedNonStudent} = isUnlicensedTeacherOrAdmin();
  /*
   * when a user can't access the guide level, we need to run a function instead of link
   * right now, this is for an assessment guide, with unlicensed, logged out, or student users
   */
  const teacherOrAdminWithoutAccess = isUnlicensedNonStudent && !subject.doesUserHaveAccess();
  let isButton;
  let buttonOnClick;
  if (!sessionStore.hasValidSession() && isAssessment) {
    isButton = true;
    buttonOnClick = showGlobalLoginModal;
  } else if (
    isAccessCheckReady &&
    isAssessment &&
    (isStudentWithoutAccess || teacherOrAdminWithoutAccess)
  ) {
    isButton = true;
    buttonOnClick = openUpgradeSubjectModal;
  } else if (
    isAccessCheckReady &&
    !licensedSubjects.isEmpty() &&
    !subject.doesUserHaveAccess() &&
    sessionStore.hasValidSession()
  ) {
    isButton = true;
    buttonOnClick = openUpgradeSubjectModal;
  }

  const guideLevelDisabled =
    usePublicView && !guideLevel.isFree() && (guideLevel.isTheme() || hasSubtopics);

  let typeProps: Record<string, any> = guideLevelDisabled
    ? {as: 'li'}
    : {
        as: Link,
        to: getStudyGuideLink(linkToQuestions, queryString)
      };
  if (usePublicView && !guideLevelDisabled && isSearchMode()) {
    typeProps = {
      as: 'li',
      onClick: openUpgradeSubjectModal,
      tabIndex: '0',
      role: 'button',
      // @ts-expect-error -- pre-existing arity bug carried verbatim: `onKeyPress(e, onClick)`
      // is called with the handler in the event slot, so Enter never activates the row.
      onKeyPress: () => onKeyPress(openUpgradeSubjectModal)
    };
  } else if (isButton) {
    typeProps = {
      as: 'li',
      onClick: buttonOnClick,
      tabIndex: '0',
      role: 'button',
      // @ts-expect-error -- pre-existing arity bug carried verbatim: `onKeyPress(e, onClick)`
      // is called with the handler in the event slot, so Enter never activates the row.
      onKeyPress: () => onKeyPress(buttonOnClick)
    };
  }

  let classNames = 'decoration_none';
  if (isLastOfTheme) {
    classNames += ' sgi--last-of-theme';
  }
  if (guideLevelDisabled) {
    classNames += ' disabled';
  }

  const actionClassNames = hasTeacherAccess ? 'teacher' : 'student';
  const guideLevelRef = themeRefs[guideLevel.getId()] || null;

  return (
    <StudyGuideItem
      aria-label={`${guideLevel.getName()}${guideLevel.isFree() ? '(Free)' : ''}`}
      className={classNames}
      itemType={guideLevel.getGuideLevelType()}
      id={guideLevel.getUrlSlug()}
      {...typeProps}
    >
      {guideLevel.isTheme() ? <StudyGuideItem.Img src={guideLevel.getThemeImg()} /> : null}
      <StudyGuideItem.Content>
        <StudyGuideItem.Heading as={`h${headingLevel}`}>
          <span className='gap-x1 flex items-center'>
            <MarkdownRendererV2 text={guideLevel.getName()} />
            <GuideLevelHeadingIndicators
              guideLevel={guideLevel}
              isAssessment={isAssessment}
              teacherRestrictedGuideLevelIds={teacherRestrictedGuideLevelIds}
              guideLevelIdsWithLockedDescendant={guideLevelIdsWithLockedDescendant}
            />
          </span>
        </StudyGuideItem.Heading>
        <StudyGuideItem.Body>
          {!!guideLevel.getDescription() && (
            <MarkdownRendererV2 text={guideLevel.getDescription()} />
          )}
          <GuideLevelRestrictionBadge
            guideLevel={guideLevel}
            isAssessment={isAssessment}
            guideLevelTree={guideLevelTree}
          />
        </StudyGuideItem.Body>
        {!guideLevel.isTheme() && hasTeacherAccess ? (
          <StudyGuideItem.Meta>{metadata}</StudyGuideItem.Meta>
        ) : null}
      </StudyGuideItem.Content>
      <StudyGuideItem.Actions className={actionClassNames}>
        <GuideLevelActions
          guideLevel={guideLevel}
          guideLevelDisabled={guideLevelDisabled}
          hasTeacherAccess={hasTeacherAccess}
          hasAccessToCreateTemplate={hasAccessToCreateTemplate}
          isStudentWithoutAccess={isStudentWithoutAccess}
          isAssessment={isAssessment}
          link={linkToQuestions + queryString}
          guideLevelTree={guideLevelTree}
          metadata={metadata}
          partialCreditEnabled={partialCreditEnabled}
        />
      </StudyGuideItem.Actions>
      <span ref={guideLevelRef} />
    </StudyGuideItem>
  );
};
